interface JourneyTypeMap {
  manual: string
  clave: string
}

interface AnalyticsDefaults {
  viewChange: string
  pagename: string
  platform: string
  country: string
  language: string
  brand: string | undefined
}

interface FileUploadStatusMap {
  SUCCESS: string
  LOADING: string
  UNSUPPORTED: string
  TOO_BIG: string
  ERROR: string
}

interface ErrorsMap {
  TIMEOUT: string
  POLLING_ERROR: string
  GENERAL: string
}

interface StepMap {
  PERSONAL: string
  CONTACT: string
  EMPLOYMENT: string
  FINANCIAL: string
  DATA: string
  SUMMARY: string
}

export const BRAND_NAME = process.env.REACT_APP_WEBSITE_BRAND

export const BASE_BRAND_NAME = process.env.REACT_APP_WEBSITE_BASE_BRAND

export const PRODUCT = process.env.REACT_APP_PRODUCT

export const ENTRYPOINT = process.env.REACT_APP_ENTRYPOINT

export const CONNECTION_TIMEOUT = 30000

export const POLLING_INTERVAL = 2000

export const MAX_POLLING_CYCLES = 60

export const DATE_FORMAT = 'DD/MM/YYYY'

export const DATE_SEPARATOR = DATE_FORMAT.split('').find((item) => {
  return !item.match(/^[a-zA-Z]*$/)
})

export const NUMBER_FORMAT = 'es'

export const CURRENCY = 'EUR'

export const CURRENCY_SYMBOL = '€'

export const JOURNEY_TYPE: JourneyTypeMap = {
  manual: 'manual',
  clave: 'clave',
}

export const ANALYTICS_DEFAULTS: AnalyticsDefaults = {
  viewChange: '',
  pagename: 'Application Form',
  platform: 'operatelease',
  country: 'ES',
  language: 'es',
  brand: BRAND_NAME,
}

export const FILE_UPLOAD_STATUS: FileUploadStatusMap = {
  SUCCESS: 'SUCCESS',
  LOADING: 'LOADING',
  UNSUPPORTED: 'UNSUPPORTED',
  TOO_BIG: 'TOO_BIG',
  ERROR: 'ERROR',
}

export const ERRORS: ErrorsMap = {
  TIMEOUT: 'timeout',
  POLLING_ERROR: 'polling error',
  GENERAL: 'general error',
}

export const MAX_FILESIZE = 1000 * 1000 * 4

export const getAnalyticsBrand = (brand: string | undefined): string | undefined => {
  if (!brand || !brand.length) {
    return ANALYTICS_DEFAULTS.brand
  }
  return brand.toLowerCase() === 'volkswagen' ? 'vw' : brand.toLowerCase()
}

export const ADOBE_API_KEY = process.env.REACT_APP_ADOBE_API_KEY

const CSBR_BRANDS = ['vwfs']

// some aspects of the visual presentation are different for CSbR
export const CSBR_MODE_ENABLED =
  CSBR_BRANDS.includes(BASE_BRAND_NAME || '') && BRAND_NAME === 'vwfscsbr'

export const isACS = (entrypoint: string | undefined): boolean => entrypoint === 'acs'

export const isVTP = (entrypoint: string | undefined): boolean => entrypoint === 'vtp'

export const isCSBR = (entrypoint: string | undefined): boolean => {
  return (
    [BASE_BRAND_NAME, BRAND_NAME].includes('vwfscsbr') ||
    isACS(entrypoint) ||
    isVTP(entrypoint)
  )
}

export const STEP: StepMap = {
  PERSONAL: 'personalDetails',
  CONTACT: 'contactDetails',
  EMPLOYMENT: 'occupationalDetails',
  FINANCIAL: 'financialDetails',
  DATA: 'dataProtectionDetails',
  SUMMARY: 'summary',
}

const mapBrand = (brand: string | undefined): string | null => {
  switch (brand) {
    case 'cupra':
      return 'Cupra'
    case 'vwfs':
    case 'vwn':
    case 'vw':
      return 'VW'
    case 'audi':
      return 'Audi'
    case 'seat':
      return 'Seat'
    case 'skoda':
      return 'Skoda'
    default:
      return null
  }
}

export const getAnalyticsContext = (
  entrypoint: string | undefined = ENTRYPOINT
): string | null => {
  const mappedBrand = mapBrand(BASE_BRAND_NAME)
  switch (entrypoint) {
    case 'acs':
      return `ACS ${mappedBrand}`
    case 'vtp':
      return `VTP ${mappedBrand}`
    case 'd2o':
    case 'dealershop':
      return `Dealershop ${mappedBrand}`
    case 'oneshop':
      return `ONE.Shop ${mappedBrand}`
    default:
      return null
  }
}

const ariaLabelMap = new Map([
  ['audi', 'Audi logo'],
  ['vw', 'Volkswagen logo'],
  ['vwcv', 'Volkswagen Vehículos Comerciales logo'],
  ['vwn', 'Volkswagen Vehículos Comerciales logo'],
  ['vwfs', 'Volkswagen Financial Services logo'],
  ['skoda', 'Skoda logo'],
  ['seat', 'Seat logo'],
  ['cupra', 'Cupra logo'],
])

export const getAltLabel = (brandName: string | undefined): string => {
  if (!brandName) return 'Logo'
  const normalizedBrandName = brandName.toLowerCase()
  return ariaLabelMap.get(normalizedBrandName) || 'Logo'
}
